/**
 * Headline tile for the store overview.
 *
 * WHY money totals are a list and not one number: settlement currencies do not add up. A shop
 * paid out in USD and CAD has two held-funds figures, and summing them would be a lie — so the
 * tile shows one row per currency and says so when there are none.
 */
import type { ReactNode } from "react";
import type { MoneyTotal } from "../api";
import { Amount } from "./Amount";
import { formatCount } from "../money";

export function Tile({ label, hint, children, tone = "neutral" }: {
  label: string;
  hint?: string;
  children: ReactNode;
  tone?: "neutral" | "warn" | "bad";
}): ReactNode {
  return (
    <section className={`tile tile-${tone}`}>
      <h2 className="tile-label">{label}</h2>
      <div className="tile-body">{children}</div>
      {hint ? <p className="tile-hint">{hint}</p> : null}
    </section>
  );
}

export function MoneyTotals({ totals, emptyLabel }: { totals: MoneyTotal[]; emptyLabel: string }): ReactNode {
  if (totals.length === 0) return <p className="tile-none">{emptyLabel}</p>;
  return (
    <ul className="totals">
      {totals.map((t) => (
        <li key={t.currency}>
          <Amount minor={t.amount_minor} currency={t.currency} strong />
          <span className="totals-count">{formatCount(t.count)} {t.count === "1" ? "item" : "items"}</span>
        </li>
      ))}
    </ul>
  );
}

export function BigNumber({ value, suffix }: { value: string; suffix?: string }): ReactNode {
  return (
    <p className="tile-number">
      {formatCount(value)}
      {suffix ? <span className="tile-number-suffix"> {suffix}</span> : null}
    </p>
  );
}
